import { useCallback, useState } from 'react';
import {
  SdkErrorResponse,
  pushErrorFromAPI,
} from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';
import { getReportDownloadAPI } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsWrapper/api/getReportDownloadAPI';
import { getWindow } from 'src/utilities/window';

interface UseDownloadReportProps {
  corpId: string;
  exportId: string;
}

export interface UseDownloadReportReturn {
  downloadReport: () => Promise<void>;
  isDownloading: boolean;
}

export const useDownloadReport = ({
  corpId,
  exportId,
}: UseDownloadReportProps): UseDownloadReportReturn => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadReport = useCallback(async (): Promise<void> => {
    try {
      setIsDownloading(true);
      const { presignedUrl } = await getReportDownloadAPI(corpId, exportId);
      // Presigned S3 URLs are not fetchable from the app origin without bucket CORS.
      // Navigating the current tab triggers a normal download when S3 sends Content-Disposition: attachment.
      getWindow()?.location?.assign(presignedUrl);
    } catch (error) {
      pushErrorFromAPI(error as IErrorResponse | SdkErrorResponse, {
        fallbackMessage: 'There was a problem downloading the report.',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [corpId, exportId]);

  return { downloadReport, isDownloading };
};
